import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import colors from '../../../../assets/theme/base/colors';
import { IEFSubCategoryConfiguration } from '../../../../Component/Model/Dto/EmpowermentFinancing';
import { formatRatioTotal } from '../../../../Helpers/EmpowermentFinancing/formatHelper';
import { SubHeading } from './TargetSettingsLabels';

interface IRatioInputProps {
	label: string;
	value: number;
	onChange: (value: number) => void;
}

const RatioInput = ({ label, value, onChange }: IRatioInputProps) => {
	const share = Math.min(Math.max(value, 0), 100);

	return (
		<Box
			sx={{
				py: 1.25,
				borderBottom: '1px solid',
				borderColor: 'grey.200',
				'&:last-of-type': { borderBottom: 'none' },
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
					{label}
				</Typography>
				<TextField
					size="small"
					type="number"
					inputProps={{ min: 0, max: 100, step: 0.1 }}
					value={value}
					onChange={e => onChange(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									%
								</Typography>
							</InputAdornment>
						),
					}}
					sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.8125rem', textAlign: 'right' } }}
				/>
			</Box>
			<Box
				sx={{
					mt: 0.75,
					height: 4,
					borderRadius: 2,
					bgcolor: colors.empowermentFinancing.proportionBarTrack,
					overflow: 'hidden',
				}}
			>
				<Box sx={{ height: '100%', width: `${share}%`, bgcolor: 'primary.main', transition: 'width 0.2s' }} />
			</Box>
		</Box>
	);
};

interface IProps {
	subCategories: IEFSubCategoryConfiguration[];
	ratios: Record<string, number>;
	onRatioChange: (key: string, value: number) => void;
	onEqualAllocation: () => void;
	ratioTotal: number;
	isRatioTotalValid: boolean;
}

export const SubRatiosPanel = ({
	subCategories,
	ratios,
	onRatioChange,
	onEqualAllocation,
	ratioTotal,
	isRatioTotalValid,
}: IProps) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
			<SubHeading>Targeted Investment sub-ratios</SubHeading>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
				<Button
					size="small"
					variant="outlined"
					onClick={onEqualAllocation}
					sx={{ py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none', minWidth: 0 }}
				>
					Equal Allocation
				</Button>
				<Box
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						px: 1,
						py: 0.25,
						borderRadius: 1,
						bgcolor: isRatioTotalValid
							? colors.empowermentFinancing.ratioValidBackground
							: colors.empowermentFinancing.ratioInvalidBackground,
					}}
				>
					<Typography
						variant="caption"
						sx={{
							fontWeight: 700,
							color: isRatioTotalValid
								? colors.empowermentFinancing.ratioValidText
								: colors.empowermentFinancing.ratioInvalidText,
						}}
					>
						Total: {formatRatioTotal(ratioTotal)}
						{isRatioTotalValid ? '' : ' - must be 100%'}
					</Typography>
				</Box>
			</Box>
		</Box>
		<Box
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				border: '1px solid',
				borderColor: colors.ui.border,
				borderRadius: 1,
				bgcolor: colors.empowermentFinancing.alternateRowBackground,
				px: 2,
				py: 1,
			}}
		>
			{subCategories.map(subCategory => (
				<RatioInput
					key={subCategory.key}
					label={subCategory.name}
					value={ratios[subCategory.key] ?? 0}
					onChange={value => onRatioChange(subCategory.key, value)}
				/>
			))}
		</Box>
	</Box>
);
